import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { clearRedirectUrl, clearError, setEntraAuthSuccess } from '@/store/slices/authSlice'
import { loginUser } from '@/store/thunks/authThunks'
import { getDefaultRedirectPath, UserRole } from '@/utils/roleUtils'
import { loginSchema, LoginRequest } from '@shared'
import { FormField } from '@/components/ui/FormField'
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react'
import EntraLoginButton from '@/components/auth/EntraLoginButton'
import entraAuthService from '@/services/entraAuthService'
import { store } from '@/store/store'
import { transformUser } from '@/utils/userTransform'

const LoginPage: React.FC = () => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { isAuthenticated, isLoading, error, redirectUrl, user } = useAppSelector((state) => state.auth)
  
  const [showPassword, setShowPassword] = useState(false)
  const [processingAuthCallback, setProcessingAuthCallback] = useState(false)

  // React Hook Form setup
  const methods = useForm<LoginRequest>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    },
    mode: 'onBlur'
  })

  const { handleSubmit, formState: { isSubmitting }, watch } = methods
  const values = watch()

  // Clear any existing errors when component mounts
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  // Handle auth callback if this page was loaded as redirect from Azure AD
  useEffect(() => {
    const handleAuthCallback = async () => {
      // Check if URL contains auth callback parameters
      const urlParams = new URLSearchParams(location.search)
      const hasAuthParams = urlParams.has('code') || urlParams.has('error')
      
      if (hasAuthParams) {
        console.log('🔄 Detected auth callback on login page')
        setProcessingAuthCallback(true)
        
        try {
          // Handle redirect response
          const account = await entraAuthService.handleRedirectPromise()
          console.log('✅ Auth callback result:', account)
          
          if (account) {
            // Set active account
            entraAuthService.setActiveAccount(account)
            
            // Get ID token
            const idToken = await entraAuthService.getTokenSilently(account)
            
            // Verify with backend
            console.log('🔄 Sending token to backend for verification...')
            console.log('🔄 Token preview:', idToken.substring(0, 50) + '...')
            
            try {
              const response = await entraAuthService.verifyTokenWithBackend(idToken)
              console.log('✅ Backend verification successful:', response)
              console.log('✅ Response structure:', {
                hasUser: !!response.user,
                hasToken: !!response.token,
                userEmail: response.user?.email,
                userRole: response.user?.role,
                message: response.message
              })
              
              if (!response.user || !response.token) {
                throw new Error('Backend response missing user or token data')
              }
              
              // Update Redux state
              console.log('🔄 Updating Redux state with user data:', {
                user: response.user,
                hasToken: !!response.token,
                tokenPreview: response.token?.substring(0, 20) + '...'
              })
              
              const transformedUser = transformUser(response.user)
              const actionResult = dispatch(setEntraAuthSuccess({
                user: transformedUser,
                token: response.token
              }))
              
              console.log('✅ Redux action dispatched:', actionResult)
              
              // Clear URL parameters
              window.history.replaceState({}, document.title, location.pathname)
              
              console.log('🔄 Auth state should now be updated, checking...')
              // Force a small delay to let Redux update
              setTimeout(() => {
                console.log('📊 Current auth state:', {
                  isAuthenticated: store.getState().auth.isAuthenticated,
                  user: store.getState().auth.user,
                  hasToken: !!store.getState().auth.token
                })
              }, 100)
              
            } catch (backendError) {
              console.error('❌ Backend verification failed:', backendError)
              throw backendError
            }
            
            console.log('✅ Authentication flow completed successfully!')
            return // Exit after successful authentication
            
          } else {
            console.log('❌ No account found in auth callback')
          }
        } catch (error) {
          console.error('❌ Auth callback failed:', error)
          console.error('❌ Error details:', {
            name: (error as Error)?.name,
            message: (error as Error)?.message,
            response: (error as any)?.response?.data,
            status: (error as any)?.response?.status,
            stack: (error as Error)?.stack
          })
        } finally {
          setProcessingAuthCallback(false)
        }
      }
    }

    handleAuthCallback()
  }, [location.search, dispatch])

  // If already authenticated, redirect to intended page or role-based default
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(clearRedirectUrl())
    }
  }, [isAuthenticated, dispatch])

  if (isAuthenticated && user) {
    // Use stored redirect URL if it exists, otherwise use role-based default
    const redirectTo = redirectUrl || location.state?.from?.pathname || getDefaultRedirectPath(user.role as UserRole)
    return <Navigate to={redirectTo} replace />
  }

  const onSubmit = async (data: LoginRequest) => {
    // Clear any existing errors
    dispatch(clearError())

    // Dispatch the login thunk
    dispatch(loginUser({
      email: data.email,
      password: data.password
    }))
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Show loading state when processing auth callback
  if (processingAuthCallback) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Completing sign in...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full mx-4">
        <div className="bg-card border rounded-lg shadow-sm p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-foreground">Welcome Back</h1>
            <p className="text-muted-foreground mt-2">Sign in to your account</p>
          </div>

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Email Field */}
              <FormField
                name="email"
                label="Email"
                type="email"
                placeholder="Enter your email"
                disabled={isLoading || isSubmitting}
                required
              />

              {/* Password Field */}
              <FormField
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                disabled={isLoading || isSubmitting}
                required
                rightIcon={showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                onRightIconClick={togglePasswordVisibility}
                rightIconTabIndex={-1}
              />

              {/* Server Error Message */}
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm p-3 rounded-md flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || isSubmitting || !values.email || !values.password}
                className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {(isLoading || isSubmitting) ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </>
                )}
              </button>
            </form>
          </FormProvider>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-muted-foreground/20"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          {/* Entra ID Login */}
          <EntraLoginButton />
          
          <div className="text-center text-sm text-muted-foreground mt-4">
            Only company email addresses are allowed
          </div>

        </div>
      </div>
    </div>
  )
}

export default LoginPage 